import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../auth/AuthContext';

function MentorStudents() {
  const { user } = useAuth();
  const [mentorId, setMentorId] = useState(null);
  const [mentorError, setMentorError] = useState(null);
  const [students, setStudents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    course: '',
    campus: '',
    hostel: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      if (!user?.email) return;
      try {
        const { data, error } = await supabase
          .from('mentors')
          .select('id')
          .eq('email', user.email)
          .maybeSingle();
        if (!error && data?.id) {
          setMentorId(data.id);
          setMentorError(null);
        } else {
          setMentorError('Mentor profile not found. Please contact an administrator.');
        }
      } catch (error) {
        console.error('Error fetching mentor profile:', error);
        setMentorError('Failed to load mentor profile. Please try refreshing the page.');
      }
    })();
  }, [user]);

  const fetchStudents = useCallback(async () => {
    if (!mentorId) return;
    const { data, error } = await supabase
      .from('students')
      .select('id,name,phone,unique_id,created_at')
      .eq('mentor_id', mentorId)
      .order('created_at', { ascending: false });
    if (!error && data) setStudents(data);
  }, [mentorId]);

  useEffect(() => {
    if (mentorId) {
      fetchStudents();
    }
  }, [mentorId, fetchStudents]);

  const filteredStudents = students.filter(student =>
    student.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    student.phone?.includes(searchQuery) ||
    student.unique_id?.toLowerCase().includes(searchQuery.toLowerCase())
  ).filter(student => {
    if (filters.course && student.course !== filters.course) return false;
    if (filters.campus && student.campus !== filters.campus) return false;
    if (filters.hostel && student.hostel !== filters.hostel) return false;
    return true;
  });

  const filterOptions = React.useMemo(() => {
    const options = {
      course: [],
      campus: [],
      hostel: []
    };

    students.forEach(student => {
      if (student.course && !options.course.includes(student.course)) {
        options.course.push(student.course);
      }
      if (student.campus && !options.campus.includes(student.campus)) {
        options.campus.push(student.campus);
      }
      if (student.hostel && !options.hostel.includes(student.hostel)) {
        options.hostel.push(student.hostel);
      }
    });

    options.course.sort();
    options.campus.sort();
    options.hostel.sort();

    return options;
  }, [students]);

  return (
    <div className="space-y-8">
      <header className="space-y-2">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">My Students</h1>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          View and manage all students assigned to you.
        </p>
      </header>

      {mentorError && (
        <section className="rounded-2xl border border-rose-200 bg-rose-50 p-6 dark:border-rose-400/40 dark:bg-rose-500/10">
          <h2 className="text-lg font-semibold text-rose-900 dark:text-rose-200">Profile Error</h2>
          <p className="mt-1 text-sm text-rose-700 dark:text-rose-300">{mentorError}</p>
        </section>
      )}

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <label className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">🔍</span>
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search students by name, phone, or EPS ID..."
                className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-10 pr-3 text-sm text-slate-900 shadow-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/40 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
              />
            </label>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {filteredStudents.length} of {students.length} students
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            {filterOptions.course.length > 0 && (
              <select
                value={filters.course}
                onChange={(e) => setFilters(prev => ({ ...prev, course: e.target.value }))}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/40 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
              >
                <option value="">All Courses</option>
                {filterOptions.course.map(course => (
                  <option key={course} value={course}>{course}</option>
                ))}
              </select>
            )}

            {filterOptions.campus.length > 0 && (
              <select
                value={filters.campus}
                onChange={(e) => setFilters(prev => ({ ...prev, campus: e.target.value }))}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/40 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
              >
                <option value="">All Campuses</option>
                {filterOptions.campus.map(campus => (
                  <option key={campus} value={campus}>{campus}</option>
                ))}
              </select>
            )}

            {filterOptions.hostel.length > 0 && (
              <select
                value={filters.hostel}
                onChange={(e) => setFilters(prev => ({ ...prev, hostel: e.target.value }))}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/40 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
              >
                <option value="">All Hostels</option>
                {filterOptions.hostel.map(hostel => (
                  <option key={hostel} value={hostel}>{hostel}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div className="mt-6">
          {filteredStudents.length === 0 ? (
            students.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-300 px-4 py-10 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
                {mentorId ? 'No students assigned yet. Refresh when assignments update.' : 'Loading your mentor profile…'}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-slate-300 px-4 py-10 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
                No students match your search.
              </div>
            )
          ) : (
            <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filteredStudents.map((student) => (
                <div
                  key={student.id}
                  onClick={() => navigate(`/students/${student.id}`)}
                  className="group relative bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 sm:p-5 shadow-sm hover:shadow-md hover:border-brand/50 dark:hover:border-brand/40 transition-all duration-200 cursor-pointer active:scale-95"
                >
                  {/* Status indicator */}
                  <div className="absolute top-3 right-3">
                    <div className="w-3 h-3 rounded-full bg-slate-300 dark:bg-slate-600 group-hover:bg-brand/60 transition-colors"></div>
                  </div>

                  {/* Student info */}
                  <div className="pr-6">
                    <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-white mb-2 leading-tight">
                      {student.name || 'Unnamed student'}
                    </h3>

                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <span className="text-sm text-slate-600 dark:text-slate-300 truncate">
                          {student.phone || 'No phone'}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                        <span className="text-sm text-slate-600 dark:text-slate-300 truncate">
                          {student.unique_id || 'No EPS ID'}
                        </span>
                      </div>

                      {(student.course || student.campus) && (
                        <div className="flex items-center gap-2 pt-1 border-t border-slate-100 dark:border-slate-700">
                          <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                          <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
                            {student.course || student.campus}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Hover indicator */}
                  <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <svg className="w-5 h-5 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default MentorStudents;
